import {
  Button,
  Card,
  makeStyles,
  tokens,
  Title3,
} from '@fluentui/react-components';
import { CheckmarkCircle32Filled, Home20Regular } from '@fluentui/react-icons';
import { SubmissionResult } from '../types';
import { riskLevelColor, formatDateAU, formatTimeAU } from '../services/risk';

interface Props {
  result: SubmissionResult;
  jobNumber: string;
  onBackToJobs: () => void;
}

const useStyles = makeStyles({
  body: {
    flex: 1,
    overflowY: 'auto',
    paddingTop: '20px',
    paddingBottom: '20px',
    paddingLeft: '18px',
    paddingRight: '18px',
    display: 'flex',
    flexDirection: 'column',
  },
  okIcon: {
    display: 'block',
    marginTop: '24px',
    marginBottom: '14px',
    marginLeft: 'auto',
    marginRight: 'auto',
    color: '#2E7D32',
    width: '64px',
    height: '64px',
  },
  title: {
    textAlign: 'center',
    color: '#2E7D32',
    marginBottom: '8px',
  },
  subtitle: {
    textAlign: 'center',
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    lineHeight: 1.5,
    marginBottom: '16px',
  },
  card: {
    paddingTop: '16px',
    paddingBottom: '16px',
    paddingLeft: '16px',
    paddingRight: '16px',
    marginBottom: '14px',
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow2,
    background: tokens.colorNeutralBackground1,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '6px',
    paddingBottom: '6px',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
    fontSize: '13px',
    gap: '8px',
    ':last-child': { borderBottomStyle: 'none' },
  },
  rowKey: { color: tokens.colorNeutralForeground3, flexShrink: 0 },
  rowVal: { fontWeight: 500, textAlign: 'right' },
  cta: {
    width: '100%',
    marginTop: '8px',
    minHeight: '52px',
    fontSize: '15px',
    fontWeight: 600,
    borderRadius: '18px',
    boxShadow: '0 10px 22px rgba(227,39,45,0.18)',
  },
});

export function DoneScreen({ result, jobNumber, onBackToJobs }: Props) {
  const styles = useStyles();
  const submittedDate = new Date(result.submittedAt);
  return (
    <div className={styles.body} data-screen-body>
      <CheckmarkCircle32Filled className={styles.okIcon} />
      <Title3 className={styles.title}>Take 5 signed & submitted</Title3>
      <div className={styles.subtitle}>
        PDF attached to the job{result.sharePointItemId ? ' · safety record saved' : ' · safety record confirmation pending'}
      </div>
      <Card className={styles.card}>
        <div className={styles.row}>
          <span className={styles.rowKey}>Document</span>
          <span className={styles.rowVal} style={{ fontSize: '11px', fontFamily: 'monospace' }}>{result.pdfFileName}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.rowKey}>Job</span>
          <span className={styles.rowVal}>{jobNumber}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.rowKey}>Submitted</span>
          <span className={styles.rowVal}>{formatTimeAU(submittedDate)} · {formatDateAU(submittedDate)}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.rowKey}>Highest risk</span>
          <span className={styles.rowVal} style={{ color: riskLevelColor(result.maxRiskLevel) }}>
            {result.maxRiskScore} ({result.maxRiskLevel})
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.rowKey}>Ascora attachment</span>
          <span className={styles.rowVal} style={{ color: '#2E7D32' }}>
            {result.ascoraAttachmentId ? "Saved" : "Confirmation pending"}
          </span>
        </div>
        {result.sharePointItemId && (
          <div className={styles.row}>
            <span className={styles.rowKey}>Safety record</span>
            <span className={styles.rowVal} style={{ color: '#2E7D32' }}>
              ✓ #{result.sharePointItemId}
            </span>
          </div>
        )}
      </Card>
      <Button
        appearance="primary"
        size="large"
        icon={<Home20Regular />}
        onClick={onBackToJobs}
        className={styles.cta}
      >
        Back to jobs
      </Button>
    </div>
  );
}
